import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  useDeleteMarketplaceItemMutation,
  useExpressMarketplaceInterestMutation,
  useUpdateMarketplaceItemMutation,
} from "../../../../store/slices/api";
import ImageLightbox from "../../Bulletin/Posts/ImageLightbox";
import PayOptions, { hasPayOptions } from "./PayOptions";
import EditItem from "./EditItem";
import { formatPrice, itemCategoryLabel, sellerName } from "./itemFormat";
import { sellerMailto } from "../payLinks";

dayjs.extend(utc);
dayjs.extend(timezone);

const DOC_EXTENSIONS = [
  "pdf", "doc", "docx", "xls", "xlsx", "txt", "csv", "zip",
  "ppt", "pptx", "rar", "7z", "rtf", "odt", "ods", "odp",
];

function getEffectiveType(attachment) {
  const ext = attachment.original_name?.split(".").pop()?.toLowerCase();
  if (DOC_EXTENSIONS.includes(ext)) return "raw";
  return attachment.resource_type;
}

// Everything Tab can land on inside the listing, for the focus trap.
const FOCUSABLE = [
  "button:not([disabled])",
  "a[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "video[controls]",
  "audio[controls]",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

function timezoneAbbr() {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" })
      .formatToParts(new Date())
      .find((part) => part.type === "timeZoneName")?.value || ""
  );
}

/**
 * The full listing, over the page: large photos, description, seller, how to
 * pay, "Message seller", and the owner's own tools.
 *
 * Drawn through a portal so it is never trapped inside a card, locks the page
 * behind it, closes on Esc or a click outside, and hands focus back to the
 * tile that opened it.
 */
function ItemDetail({ item, individualId, onClose }) {
  const navigate = useNavigate();
  const isOwner = Boolean(individualId && item.individual_id === individualId);
  // A photo that fails to load is dropped, so the viewer never shows a
  // broken image; if none load, the "No photo" placeholder shows instead.
  const [brokenPhotos, setBrokenPhotos] = useState([]);
  const markBroken = (url) =>
    setBrokenPhotos((prev) => (prev.includes(url) ? prev : [...prev, url]));
  const photos = (item.image_urls || []).filter((url) => !brokenPhotos.includes(url));
  const [chosenPhoto, setPhotoIndex] = useState(0);
  const photoIndex = Math.min(chosenPhoto, Math.max(photos.length - 1, 0));
  const [lightbox, setLightbox] = useState(null); // { images, index } or null
  const lightboxOpen = useRef(false);
  lightboxOpen.current = lightbox !== null;
  const closeButton = useRef(null);
  const panel = useRef(null);

  const [deleteItem, { isLoading: deleting }] = useDeleteMarketplaceItemMutation();
  const [updateItem, { isLoading: updating }] = useUpdateMarketplaceItemMutation();
  const [soldError, setSoldError] = useState(false);
  const [deleteError, setDeleteError] = useState(false);
  // "Are you sure?" shown in place, before the sold flag changes either way.
  const [confirmingSold, setConfirmingSold] = useState(false);
  const confirmOpen = useRef(false);
  confirmOpen.current = confirmingSold;
  const cancelSoldButton = useRef(null);
  const markSoldButton = useRef(null);
  const wasConfirming = useRef(false);

  // Focus Cancel when the question appears; hand focus back to the button
  // that asked when it goes away.
  useEffect(() => {
    if (confirmingSold) {
      if (cancelSoldButton.current) cancelSoldButton.current.focus();
    } else if (wasConfirming.current && markSoldButton.current) {
      markSoldButton.current.focus();
    }
    wasConfirming.current = confirmingSold;
  }, [confirmingSold]);
  const [expressInterest, { isLoading: interestLoading }] =
    useExpressMarketplaceInterestMutation();
  const [interestSent, setInterestSent] = useState(null);
  // The owner's edit form, shown in place of the owner tools.
  const [editing, setEditing] = useState(false);
  const editingOpen = useRef(false);
  editingOpen.current = editing;
  const editButton = useRef(null);
  const wasEditing = useRef(false);
  // When the form closes (Save, Cancel or Esc), the keyboard goes back to
  // "Edit listing" instead of falling out of the window.
  useEffect(() => {
    if (wasEditing.current && !editing && editButton.current) editButton.current.focus();
    wasEditing.current = editing;
  }, [editing]);
  // A click outside closes the window only if it also STARTED outside: a
  // drag-select in a field released over the backdrop is not a click outside.
  const pressStartedOnBackdrop = useRef(false);

  // Lock the page behind, focus the close button, and restore focus after.
  useEffect(() => {
    const opener = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (closeButton.current) closeButton.current.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (opener && typeof opener.focus === "function") opener.focus();
    };
  }, []);

  // Esc closes (unless a photo is open full-screen: then Esc closes the photo
  // only). Tab stays inside the panel.
  useEffect(() => {
    const onKey = (e) => {
      if (lightboxOpen.current) return;
      if (e.key === "Escape" && confirmOpen.current) {
        // Esc answers "Cancel" to the sold question; the listing stays open.
        e.preventDefault();
        setConfirmingSold(false);
        return;
      }
      if (e.key === "Escape" && editingOpen.current) {
        // Esc cancels the edit; the listing stays open so nothing typed is
        // lost to a stray keypress closing the whole window.
        e.preventDefault();
        setEditing(false);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
      if (e.key === "Tab" && panel.current) {
        const focusable = Array.from(panel.current.querySelectorAll(FOCUSABLE));
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleInterest = async (kind) => {
    try {
      const res = await expressInterest({ id: item.id, kind }).unwrap();
      setInterestSent(res?.emailed ? kind : "not_configured");
    } catch {
      setInterestSent("error");
    }
  };

  const handleToggleSold = async () => {
    setSoldError(false);
    try {
      await updateItem({ id: item.id, is_sold: !item.is_sold }).unwrap();
      setConfirmingSold(false);
    } catch {
      setSoldError(true);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this listing?")) return;
    setDeleteError(false);
    try {
      await deleteItem({ id: item.id, individual_id: individualId }).unwrap();
      onClose();
    } catch {
      setDeleteError(true);
    }
  };

  const price = formatPrice(item.price);
  const attachments = item.attachments || [];
  // An attached photo that won't load moves to the file list, so its link
  // is still there instead of a broken thumbnail.
  const isShownPhoto = (a) => getEffectiveType(a) === "image" && !brokenPhotos.includes(a.url);
  const attachedImages = attachments.filter(isShownPhoto);
  const attachedVideos = attachments.filter((a) => getEffectiveType(a) === "video");
  const attachedDocs = attachments.filter((a) => !isShownPhoto(a) && getEffectiveType(a) !== "video");

  const sectionRule = "border-t border-dupontGold/30 dark:border-darkGold/15 pt-[14px]";

  return createPortal(
    <>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={`market-item-${item.id}-title`}
        onMouseDown={(e) => {
          pressStartedOnBackdrop.current = e.target === e.currentTarget;
        }}
        onClick={(e) => {
          // While the seller is editing, a stray click outside must not throw
          // away what they typed (Esc is guarded the same way).
          if (e.target === e.currentTarget && pressStartedOnBackdrop.current && !editingOpen.current) onClose();
        }}
        className="fixed inset-0 z-[70] flex items-start sm:items-center justify-center overflow-y-auto bg-[rgba(10,16,28,0.6)] backdrop-blur-[3px] p-0 sm:p-[16px] animate-fadeIn"
      >
        <div
          ref={panel}
          className="relative w-full max-w-[1040px] min-h-full max-h-full sm:min-h-0 sm:max-h-[94vh] overflow-y-auto sm:rounded-[16px] bg-white dark:bg-darkCard shadow-[0_30px_60px_-22px_rgba(0,0,0,0.5)]"
        >
          {/* Top bar */}
          <div className="sticky top-0 z-10 flex items-center justify-between gap-x-[12px] px-[16px] sm:px-[24px] py-[12px] bg-white/95 dark:bg-darkCard/95 border-b border-dupontGold/30 dark:border-darkGold/15">
            <span className="text-[13px] font-semibold uppercase tracking-[0.1em] text-dupontGoldDk dark:text-darkGold">
              {itemCategoryLabel(item)}
            </span>
            <button
              ref={closeButton}
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-x-[6px] rounded-full border border-dupontGold/50 px-[14px] py-[6px] text-[15px] font-semibold text-dupontBlue dark:text-darkText hover:bg-dupontGold/10 cursor-pointer"
            >
              <span aria-hidden="true">&times;</span> Close
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[1.15fr_1fr] gap-[20px] md:gap-[28px] p-[16px] sm:p-[24px]">
            {/* Photos */}
            <div className="flex flex-col gap-y-[10px]">
              {photos.length > 0 ? (
                <>
                  <button
                    type="button"
                    onClick={() => setLightbox({ images: photos, index: photoIndex })}
                    className="relative block w-full rounded-[12px] overflow-hidden bg-dupontCream dark:bg-darkElevated cursor-zoom-in"
                    aria-label="See this photo full screen"
                  >
                    <img
                      src={photos[photoIndex]}
                      alt={`${item.title}, photo ${photoIndex + 1} of ${photos.length}`}
                      decoding="async"
                      onError={() => markBroken(photos[photoIndex])}
                      className={"block w-full max-h-[62vh] object-contain " + (item.is_sold ? "grayscale-[60%]" : "")}
                    />
                    {item.is_sold && (
                      <span className="absolute top-[12px] left-[12px] rounded-full bg-dupontBlue text-dupontGoldLt px-[14px] py-[5px] text-[13px] font-bold uppercase tracking-[0.12em] shadow">
                        Sold
                      </span>
                    )}
                  </button>
                  {photos.length > 1 && (
                    <div className="flex flex-row flex-wrap gap-[8px]">
                      {photos.map((url, index) => (
                        <button
                          key={url + index}
                          type="button"
                          onClick={() => setPhotoIndex(index)}
                          aria-label={`Show photo ${index + 1}`}
                          aria-current={photoIndex === index ? "true" : undefined}
                          className={
                            "w-[64px] h-[64px] rounded-[8px] overflow-hidden border-2 cursor-pointer " +
                            (photoIndex === index ? "border-dupontGold" : "border-transparent opacity-80 hover:opacity-100")
                          }
                        >
                          <img src={url} alt="" loading="lazy" onError={() => markBroken(url)} className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="relative flex flex-col items-center justify-center gap-y-[6px] w-full aspect-[4/3] rounded-[12px] bg-dupontCream dark:bg-darkElevated text-dupontGold/70 dark:text-darkGold/60">
                  <span className="font-display text-[44px] leading-none" aria-hidden="true">&#10086;</span>
                  <span className="font-body italic text-[15px]">No photo</span>
                  {item.is_sold && (
                    <span className="absolute top-[12px] left-[12px] rounded-full bg-dupontBlue text-dupontGoldLt px-[14px] py-[5px] text-[13px] font-bold uppercase tracking-[0.12em] shadow">
                      Sold
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Details */}
            <div className="flex flex-col gap-y-[14px] min-w-0">
              <h2
                id={`market-item-${item.id}-title`}
                className="m-0 font-display font-bold text-[26px] sm:text-[30px] leading-tight text-dupontBlue dark:text-darkText break-words"
              >
                {item.title}
              </h2>

              <div className="flex flex-row flex-wrap items-baseline gap-x-[12px] gap-y-[4px]">
                {price ? (
                  <span className={"font-display font-bold text-[26px] " + (item.is_sold ? "text-dupontGray dark:text-darkTextSecondary" : "text-dupontBlue dark:text-darkGold")}>
                    <span className={item.is_sold ? "line-through" : ""}>{price}</span>
                    {item.price_unit && (
                      <span className="ml-[6px] text-[16px] font-body font-normal text-dupontGray dark:text-darkTextSecondary">
                        {item.price_unit}
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="font-body italic text-[18px] text-dupontGray dark:text-darkTextSecondary">
                    Make an offer
                  </span>
                )}
                {item.is_sold && (
                  <span className="text-[15px] font-semibold text-dupontBlue dark:text-darkText">
                    This item has been sold.
                  </span>
                )}
              </div>

              <div className="text-[15px] font-body text-dupontGray dark:text-darkTextSecondary">
                Offered by{" "}
                <span className="font-semibold text-dupontBlue dark:text-darkText">
                  {isOwner ? "you" : sellerName(item)}
                </span>
                {item.created_at && (
                  <>
                    {" "}&middot; Posted {dayjs(item.created_at).utc().local().format("MMM D, YYYY h:mma")} ({timezoneAbbr()})
                  </>
                )}
              </div>

              <div className="text-[17px] font-body leading-relaxed text-dupontBlue dark:text-darkText whitespace-pre-wrap break-words">
                {item.description}
              </div>

              {/* Buyer actions */}
              {!isOwner && item.individual_id && (
                <button
                  type="button"
                  onClick={() =>
                    navigate(`/connect?tab=correspondence&letters=compose&to=${item.individual_id}`)
                  }
                  className="inline-flex items-center justify-center gap-x-[8px] w-full sm:w-auto self-start rounded-[10px] border-2 border-dupontGold px-[20px] py-[10px] text-[16px] font-display font-semibold text-dupontBlue dark:text-darkGold hover:bg-dupontGold/10 cursor-pointer"
                >
                  <span aria-hidden="true">&#9993;</span> Message seller
                </button>
              )}

              {!item.is_sold && (
                <div className="flex flex-col gap-y-[6px]">
                  {isOwner && hasPayOptions(item.payment_platform, item.payment_handle) && (
                    <div className="text-[13px] font-body italic text-dupontGray dark:text-darkTextSecondary">
                      This is what buyers see:
                    </div>
                  )}
                  <PayOptions platform={item.payment_platform} handle={item.payment_handle} />
                </div>
              )}

              {/* Quick interest: emails the seller (E5). Hidden on your own / sold items. */}
              {!isOwner && !item.is_sold && (
                <div className={sectionRule}>
                  {interestSent === null ? (
                    <div className="flex flex-col gap-y-[8px]">
                      <div className="flex flex-row gap-x-[10px]">
                        <button
                          type="button"
                          disabled={interestLoading}
                          onClick={() => handleInterest("interested")}
                          className="flex-1 bg-dupontGold text-white rounded-[8px] py-[9px] px-[12px] text-[15px] font-[600] hover:opacity-90 disabled:opacity-50 cursor-pointer"
                        >
                          {"I'm interested"}
                        </button>
                        <button
                          type="button"
                          disabled={interestLoading}
                          onClick={() => handleInterest("buy")}
                          className="flex-1 bg-transparent text-dupontBlue dark:text-darkGold border border-dupontGold rounded-[8px] py-[9px] px-[12px] text-[15px] font-[600] hover:bg-dupontGold/10 disabled:opacity-50 cursor-pointer"
                        >
                          I want to buy
                        </button>
                      </div>
                      <div className="text-[13px] text-dupontGray dark:text-darkTextSecondary">
                        {"We'll email the seller for you — no need to write anything."}
                      </div>
                    </div>
                  ) : (
                    <div className="text-[15px] dark:text-darkText" aria-live="polite">
                      {interestSent === "interested" && "✓ The seller's been emailed that you're interested!"}
                      {interestSent === "buy" && "✓ The seller's been emailed that you'd like to buy it!"}
                      {interestSent === "not_configured" && "Got it — the seller will be notified once family email is switched on."}
                      {interestSent === "error" && "That didn't go through — you can use Message seller or email the seller below instead."}
                    </div>
                  )}
                </div>
              )}

              {/* Contact */}
              {item.seller_email && (
                <div className={sectionRule + " text-[15px] font-body text-dupontBlue dark:text-darkText break-words"}>
                  Seller's email:{" "}
                  {sellerMailto(item.seller_email, item.title) ? (
                    <a
                      href={sellerMailto(item.seller_email, item.title)}
                      className="text-dupontBlue dark:text-darkGold underline decoration-dupontGold/60 hover:decoration-dupontGold"
                    >
                      {item.seller_email}
                    </a>
                  ) : (
                    item.seller_email
                  )}
                </div>
              )}

              {/* Attachments */}
              {attachments.length > 0 && (
                <div className={sectionRule + " flex flex-col gap-y-[10px]"}>
                  <div className="text-[13px] font-bold text-dupontGray dark:text-darkTextSecondary uppercase tracking-wide">
                    Attachments
                  </div>
                  {attachedImages.length > 0 && (
                    <div className="flex flex-row flex-wrap gap-[8px]">
                      {attachedImages.map((img, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setLightbox({ images: attachedImages.map((a) => a.url), index: idx })}
                          className="rounded-[10px] overflow-hidden border border-dupontGold/40 shadow-sm hover:opacity-90 cursor-zoom-in"
                          aria-label={`See ${img.original_name || "attached photo"} full screen`}
                        >
                          <img src={img.url} alt={img.original_name || ""} onError={() => markBroken(img.url)} className="w-[96px] h-[96px] object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                  {attachedVideos.map((vid, idx) => (
                    <video
                      key={idx}
                      src={vid.url}
                      controls
                      className="max-w-full max-h-[300px] rounded-[10px] border border-dupontGold/40 shadow-sm"
                    >
                      Your browser does not support video playback.
                    </video>
                  ))}
                  {attachedDocs.length > 0 && (
                    <div className="flex flex-row flex-wrap gap-[8px]">
                      {attachedDocs.map((doc, idx) => {
                        const ext = doc.original_name?.split(".").pop()?.toUpperCase() || "FILE";
                        return (
                          <a
                            key={idx}
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex flex-row items-center gap-x-[8px] px-[12px] py-[8px] rounded-[10px] border border-dupontGold/30 bg-white dark:bg-darkElevated shadow-sm hover:shadow-md transition-shadow no-underline"
                          >
                            <span className="text-[10px] font-bold px-[6px] py-[2px] rounded-full bg-dupontCream dark:bg-darkAccentBg text-dupontBlue dark:text-darkText">
                              {ext}
                            </span>
                            <span className="text-[13px] text-dupontBlue dark:text-darkText truncate max-w-[200px]">
                              {doc.original_name}
                            </span>
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Owner tools */}
              {isOwner && (
                <div className={sectionRule + " flex flex-col gap-y-[12px]"}>
                  {editing ? (
                    <EditItem item={item} onDone={() => setEditing(false)} />
                  ) : confirmingSold ? (
                    <div
                      role="group"
                      aria-labelledby={`market-item-${item.id}-sold-q`}
                      aria-describedby={`market-item-${item.id}-sold-note`}
                      className="flex flex-col gap-y-[10px] rounded-[12px] border border-dupontGold/60 dark:border-darkGold/30 bg-dupontCream dark:bg-darkElevated p-[14px]"
                    >
                      <div
                        id={`market-item-${item.id}-sold-q`}
                        className="font-display font-semibold text-[17px] text-dupontBlue dark:text-darkText"
                      >
                        {item.is_sold
                          ? "Are you sure this item is available again?"
                          : "Are you sure this item was sold?"}
                      </div>
                      <div
                        id={`market-item-${item.id}-sold-note`}
                        className="text-[15px] font-body text-dupontGray dark:text-darkTextSecondary"
                      >
                        {item.is_sold
                          ? "It will show as available to the family again. You can mark it sold later."
                          : "It will show as Sold to the family. You can mark it available again later."}
                      </div>
                      <div className="flex flex-col-reverse sm:flex-row gap-[10px]">
                        <button
                          ref={cancelSoldButton}
                          type="button"
                          onClick={() => setConfirmingSold(false)}
                          className="w-full sm:w-auto rounded-[8px] border border-dupontGold px-[16px] py-[10px] text-[15px] font-semibold text-dupontBlue dark:text-darkText hover:bg-dupontGold/10 cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          disabled={updating}
                          onClick={handleToggleSold}
                          className="w-full sm:w-auto rounded-[8px] bg-dupontBlue dark:bg-darkGold text-white dark:text-dupontBlue px-[16px] py-[10px] text-[15px] font-semibold hover:opacity-90 disabled:opacity-50 cursor-pointer"
                        >
                          {updating
                            ? "Saving..."
                            : item.is_sold
                              ? "Yes, mark available again"
                              : "Yes, mark as sold"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-row flex-wrap items-center gap-[12px]">
                      <button
                        ref={markSoldButton}
                        type="button"
                        onClick={() => {
                          setSoldError(false);
                          setConfirmingSold(true);
                        }}
                        className="rounded-[8px] bg-dupontBlue dark:bg-darkGold text-white dark:text-dupontBlue px-[16px] py-[8px] text-[15px] font-semibold hover:opacity-90 cursor-pointer"
                      >
                        {item.is_sold ? "Mark available again" : "Mark as sold"}
                      </button>
                      <button
                        ref={editButton}
                        type="button"
                        onClick={() => {
                          setSoldError(false);
                          setEditing(true);
                        }}
                        className="rounded-[8px] border border-dupontGold px-[16px] py-[8px] text-[15px] font-semibold text-dupontBlue dark:text-darkText hover:bg-dupontGold/10 cursor-pointer"
                      >
                        Edit listing
                      </button>
                      <button
                        type="button"
                        disabled={deleting}
                        onClick={handleDelete}
                        className="text-[14px] text-red-700 dark:text-red-400 hover:underline disabled:opacity-50 cursor-pointer"
                      >
                        Delete listing
                      </button>
                    </div>
                  )}
                  {soldError && (
                    <span className="text-[14px] text-red-700 dark:text-red-400" aria-live="polite">
                      {"That didn't save. Please try again."}
                    </span>
                  )}
                  {deleteError && (
                    <span className="text-[14px] text-red-700 dark:text-red-400" aria-live="polite">
                      {"That listing couldn't be deleted. Please try again."}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {lightbox && (
        <ImageLightbox
          images={lightbox.images}
          startIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </>,
    document.body
  );
}

export default ItemDetail;
